import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CountryPhoneCode } from '../../_models/models';
import { PhoneValidationService } from '../../_services/phone-validation.service';
import { COUNTRY_PHONE_CODES } from './config/phone-input.config';
import { FormPartComponent } from '../form-part-component';


export interface PhoneInputModel {
  phone: string | null;
  phoneCountryCode: string | null;
}

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.less'],
  providers: FormPartComponent.createDirectiveProviders(PhoneInputComponent)
})
export class PhoneInputComponent extends FormPartComponent<PhoneInputModel, FormGroup> implements OnInit {
  @Input() placeholder = 'phone number';
  @Input() label = 'Phone Number';

  phoneForm: FormGroup;
  countryPhoneCodes: CountryPhoneCode[] = [];

  constructor(
    private fb: FormBuilder,
    private phoneValidationService: PhoneValidationService
  ) {
    super();
  }

  protected get formPart(): FormGroup {
    return this.phoneForm;
  }

  ngOnInit(): void {
    this.initForm();
    this.loadCountryPhoneCodes();
    this.setupCountryCodeChanges();
  }

  phoneValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (!(group instanceof FormGroup)) {
        return null;
      }

      const phoneControl = group.get('phone');
      const countryISOControl = group.get('phoneCountryCode');

      if (!phoneControl || !countryISOControl) {
        return null;
      }
      const phoneNumber = phoneControl.value as string;
      const countryISO = countryISOControl.value as string;
      let groupErrors: ValidationErrors | null = null;

      const existingPhoneErrors = { ...phoneControl.errors };
      delete existingPhoneErrors.invalidCharacters;
      delete existingPhoneErrors.tooShort;
      delete existingPhoneErrors.tooLong;
      delete existingPhoneErrors.invalidPhone;

      if (phoneNumber && !/^\d+$/.test(phoneNumber)) {
        existingPhoneErrors.invalidCharacters = true;
        groupErrors = { ...(groupErrors || {}), invalidCharacters: true };
      }

      const selectedCountryConfig = COUNTRY_PHONE_CODES.find(c => c.iso === countryISO);
      if (!selectedCountryConfig) {
        groupErrors = { ...(groupErrors || {}), invalidCountryIso: true };
      } else if (phoneNumber) {
        const actualDiallingCode = selectedCountryConfig.countryCode;
        const lengthValidatorFn = this.phoneValidationService.phoneNumberLengthValidator(actualDiallingCode);
        const lengthErrors = lengthValidatorFn(phoneControl);

        if (lengthErrors) {
          if (lengthErrors.tooShort) {
            existingPhoneErrors.tooShort = true;
            groupErrors = { ...(groupErrors || {}), tooShort: true };
          }
          if (lengthErrors.tooLong) {
            existingPhoneErrors.tooLong = true;
            groupErrors = { ...(groupErrors || {}), tooLong: true };
          }
        }
      }

      if (Object.keys(existingPhoneErrors).length > 0) {
        phoneControl.setErrors(existingPhoneErrors);
      } else {
        phoneControl.setErrors(null);
      }

      if (phoneControl.invalid && !groupErrors) {
        groupErrors = { ...(groupErrors || {}), phoneInputInvalid: true };
      }
      return this.phoneForm?.errors;
    };
  }

  override writeValue(value: PhoneInputModel | null): void {
    let isoCodeToSet = 'US'; // Default to US
    let phoneToSet = '';

    if (value) {
      phoneToSet = value.phone || '';
      if (value.phoneCountryCode) {
        const inputCountryCode = value.phoneCountryCode.toUpperCase();
        let foundCountry = COUNTRY_PHONE_CODES.find(c => c.iso === inputCountryCode);

        if (foundCountry) {
          isoCodeToSet = foundCountry.iso;
        } else {
          foundCountry = COUNTRY_PHONE_CODES.find(c => c.countryCode === value.phoneCountryCode);
          if (foundCountry) {
            isoCodeToSet = foundCountry.iso;
          } else {
            const extracted = this.phoneValidationService.extractCountryCodeFromPhone(value.phoneCountryCode);
            if (extracted && extracted.iso) {
              isoCodeToSet = extracted.iso;
              if (!phoneToSet && extracted.phoneNumber) {
                phoneToSet = extracted.phoneNumber;
              }
            }
          }
        }
      }
    }

    this.phoneForm.patchValue({
      phone: phoneToSet,
      phoneCountryCode: isoCodeToSet
    }, { emitEvent: false });
  }

  protected getFormPartValidationErrors(): ValidationErrors | null {
    if (this.phoneForm && this.phoneForm.errors) {
      return { ...this.phoneForm.errors };
    }
    const phoneControl = this.phoneForm?.get('phone');
    if (phoneControl && phoneControl.invalid && phoneControl.errors) {
      return { phoneInputChildInvalid: true, ...phoneControl.errors };
    }
    return null;
  }


  private initForm(): void {
    this.phoneForm = this.fb.group({
      phoneCountryCode: ['US'],
      phone: ['']
    }, { validators: this.phoneValidator() });
  }

  private loadCountryPhoneCodes(): void {
    this.countryPhoneCodes = COUNTRY_PHONE_CODES.map(code => ({
      ...code,
      label: `${code.iso} ${code.countryCode}`
    }));
  }

  private setupCountryCodeChanges(): void {
    const phoneControl = this.phoneForm.get('phone');
    const countryCodeControl = this.phoneForm.get('phoneCountryCode');

    if (phoneControl && countryCodeControl) {
      const countryCodeSubscription: Subscription = countryCodeControl.valueChanges
        .subscribe(() => {
          this.phoneForm.updateValueAndValidity({ emitEvent: false });
          if (this.phoneForm.invalid) {
            this.phoneForm.markAsTouched();
            this.phoneForm.markAsDirty();
          }

          if (phoneControl.invalid) {
            phoneControl.markAsDirty();
            phoneControl.markAsTouched();
          }
        });
      this.subscriptions.add(countryCodeSubscription);
    }
  }
}
